import axiosInstance from "@/lib/axiosInstance";
import axios from "axios";
import { MembershipPlan, MembershipPlanResponseDO } from "../types/membership";
import { CenterResponseDO, GymSocialLinks } from "@/types/center";

export type SubscriptionPricingTier = {
  tierIndex: number;
  leastMembers: number;
  maxMembers: number | null;
  maxMembersDisplay: string;
  label: "Starter" | "Growth" | "Scale" | "Pro" | "Enterprise";
  rangeLabel: string;
  monthlyPrice: number;
  annualEligible: boolean;
  annualPrice: number | null;
  formattedAnnualPrice: string | null;
  annualMonthlyEquivalent: number | null;
  formattedAnnualMonthlyEquivalent: string | null;
  annualSavings: number | null;
  formattedAnnualSavings: string | null;
  currencyCode: "NGN" | "USD";
  currencySymbol: "₦" | "$";
  formattedMonthlyPrice: string;
  localCurrencyCode?: string;
  localCurrencySymbol?: string;
  formattedLocalMonthlyPrice?: string;
};

export type PortalBillingCadence = "monthly" | "annual";

export type PortalPaymentProvider = "lemonsqueezy" | "flutterwave";

export type SubscriptionCadencePricing = {
  enabled: boolean;
  amount: number | null;
  currencyCode: "NGN" | "USD";
  currencySymbol: "₦" | "$";
  formattedAmount: string | null;
};

export type SubscriptionBillingOffer = {
  label: string;
  allowedProviders: PortalPaymentProvider[];
  preferredProvider: PortalPaymentProvider;
  cadences: Record<PortalBillingCadence, SubscriptionCadencePricing>;
};

export type StandardSubscriptionPricing = {
  pricingMode?: "standard";
  region: "ng" | "affordable_africa" | "rest_of_world";
  storeKey: "ng" | "row";
  countryCode: string;
  currencyCode: "NGN" | "USD";
  currencySymbol: "₦" | "$";
  billableMemberCount: number;
  currentTier: SubscriptionPricingTier;
  tiers: SubscriptionPricingTier[];
  annualDiscountPercent: number;
  billingOffer?: SubscriptionBillingOffer;
};

export type CustomSubscriptionPricing = {
  pricingMode: "custom";
  billingCenterId: string;
  label: string;
  currencyCode: "NGN" | "USD";
  currencySymbol: "₦" | "$";
  billableMemberCount: number;
  allowedProviders: PortalPaymentProvider[];
  preferredProvider: PortalPaymentProvider;
  cadences: Record<PortalBillingCadence, SubscriptionCadencePricing>;
  tiers: [];
  currentTier: null;
  customConfigId?: string;
};

export type SubscriptionPricing =
  | StandardSubscriptionPricing
  | CustomSubscriptionPricing;

export type ScheduledMembershipStartSettings = {
  allowScheduledMembershipStarts: boolean;
  scheduledMembershipStartWindowDays: number;
};

export type ScheduledMembershipStartSettingsResponse =
  ScheduledMembershipStartSettings & {
    message: string;
  };

export type PortalCheckoutRequest =
  | boolean
  | {
      provider?: "lemonsqueezy";
      billingCadence?: PortalBillingCadence;
    }
  | {
      provider: "flutterwave";
      billingCadence: PortalBillingCadence;
    };

export type PortalCheckoutResponse = {
  checkoutUrl: string | null;
  provider?: "lemonsqueezy" | "flutterwave" | "free";
  status?: "active";
  billingIntentId?: string;
  txRef?: string;
  amount?: number;
  currency?: string;
  billingCadence?: PortalBillingCadence;
  billableMemberCount?: number;
  nextBillingDate?: string;
  expiresAt?: string;
};

export type PortalCheckoutError = Error & {
  code?: string;
  nextBillingDate?: string;
  extensionOpensAt?: string;
};

const createPortalCheckoutError = (error: unknown) => {
  if (
    axios.isAxiosError(error) ||
    (typeof error === "object" && error !== null && "response" in error)
  ) {
    const responseData = (
      error as {
        response?: {
          data?: {
            message?: string;
            error?: string;
            code?: string;
            nextBillingDate?: string;
            extensionOpensAt?: string;
          };
        };
      }
    ).response?.data;
    const checkoutError = new Error(
      responseData?.message || responseData?.error || "Mutation Failed",
    ) as PortalCheckoutError;
    checkoutError.code = responseData?.code;
    checkoutError.nextBillingDate = responseData?.nextBillingDate;
    checkoutError.extensionOpensAt = responseData?.extensionOpensAt;
    return checkoutError;
  }

  return new Error("An unexpected error occurred during checkout");
};

// Members service
export const settingService = {
  async fetchFitnessCenterInfo() {
    try {
      const response = await axiosInstance.get<CenterResponseDO>(
        "/settings/fitness-center"
      );
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Fetch Failed");
      }
      throw new Error(
        "An unexpected error occurred during fetch for fitness center info"
      );
    }
  },

  async fetchMembershipPlans() {
    try {
      const response = await axiosInstance.get<MembershipPlanResponseDO>(
        "/settings/membership-plans"
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Fetch Failed");
      }
      throw new Error(
        "An unexpected error occurred during fetch for membership plan"
      );
    }
  },

  async updateScheduledMembershipStartSettings(
    settings: ScheduledMembershipStartSettings,
  ) {
    try {
      const response =
        await axiosInstance.patch<ScheduledMembershipStartSettingsResponse>(
          "/settings/fitness-center/scheduled-membership-starts",
          settings,
        );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to update scheduled membership start settings",
        );
      }
      throw new Error(
        "An unexpected error occurred while updating scheduled membership start settings",
      );
    }
  },

  async fetchSubscriptionPricing() {
    try {
      const response = await axiosInstance.get<{
        message: string;
        data: SubscriptionPricing;
      }>("/settings/subscription-pricing");
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Fetch Failed");
      }
      throw new Error(
        "An unexpected error occurred during fetch for subscription pricing"
      );
    }
  },

  async createMembershipPlan(plan: Omit<MembershipPlan, "_id" | "centerId">) {
    try {
      const response = await axiosInstance.post<{
        message: string;
        data: MembershipPlan;
      }>("/settings/membership-plans", plan);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Mutation Failed");
      }
      throw new Error(
        "An unexpected error occurred during creation of membership plan"
      );
    }
  },

  async updateMembershipPlan(id: string, updates: Partial<MembershipPlan>) {
    try {
      const response = await axiosInstance.put<{
        message: string;
        data: MembershipPlan;
      }>(`/settings/membership-plans/${id}`, updates);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Mutation Failed");
      }
      throw new Error(
        "An unexpected error occurred during update of membership plan"
      );
    }
  },

  async deleteMembershipPlan(id: string) {
    try {
      const response = await axiosInstance.delete<{
        message: string;
        data: MembershipPlan;
      }>(`/settings/membership-plans/${id}`);
      return response.data.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Mutation Failed");
      }
      throw new Error(
        "An unexpected error occurred during deletion of membership plan"
      );
    }
  },

  async createCheckoutSession(request: PortalCheckoutRequest = false) {
    try {
      const payload =
        typeof request === "boolean"
          ? request
            ? { isAnnual: true }
            : {}
          : request.provider
            ? {
                provider: request.provider,
                billingCadence: request.billingCadence ?? "monthly",
              }
            : request.billingCadence === "annual"
              ? { isAnnual: true }
              : {};

      const response = await axiosInstance.post<PortalCheckoutResponse>(
        `/payment/create-checkout-session`,
        payload,
      );
      return response.data;
    } catch (error) {
      throw createPortalCheckoutError(error);
    }
  },

  async getUpdatePaymentMethodUrl() {
    try {
      const response = await axiosInstance.get<{ url: string }>(
        `/payment/update-payment-method`,
      );
      return response.data.url;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to open payment method update",
        );
      }
      throw new Error(
        "An unexpected error occurred while opening payment method update",
      );
    }
  },

  async reconcileFlutterwaveBillingIntent(
    billingIntentId: string,
    payload: { transactionId: string; txRef: string },
  ) {
    try {
      const response = await axiosInstance.post(
        `/payment/billing-intents/flutterwave/${billingIntentId}/reconcile`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Mutation Failed");
      }
      throw new Error("Failed to reconcile Flutterwave billing payment");
    }
  },

  async switchToAnnual() {
    try {
      const response = await axiosInstance.post(`/payment/switch-to-annual`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Mutation Failed");
      }
      throw new Error("Failed to switch subscription to annual billing");
    }
  },

  async updateSubscriptionQuantity(): Promise<void> {
    try {
      const response = await axiosInstance.post(
        `/settings/update-subscription-quantity`
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "An unexpected error occurred during updating subscription quantity"
        );
      }
      throw new Error(
        "An unexpected error occurred during updating subscription quantity"
      );
    }
  },

  async uploadGymLogo(file: File) {
    try {
      const formData = new FormData();
      formData.append("logo", file);
      const response = await axiosInstance.patch<{ gymLogoUrl: string }>(
        "/settings/fitness-center/logo",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(error.response?.data?.message || "Upload failed");
      }
      throw new Error("An unexpected error occurred during logo upload");
    }
  },

  async updateSocialLinks(links: GymSocialLinks) {
    try {
      const response = await axiosInstance.patch<{
        message: string;
        data: GymSocialLinks;
      }>("/settings/fitness-center/social-links", { links });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message || "Failed to update social links",
        );
      }
      throw new Error(
        "An unexpected error occurred while updating social links",
      );
    }
  },

  async changePassword(currentPassword: string, newPassword: string) {
    try {
      const response = await axiosInstance.post<{
        message: string;
      }>(`/settings/change-password`, {
        currentPassword,
        newPassword,
      });
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new Error(
          error.response?.data?.message ||
            "Failed to change password. Please try again later."
        );
      }
      throw new Error("Failed to change password. Please try again later.");
    }
  },
};
